// A classic script, not a module, so the layout head can install window.onerror before any other script runs.

interface LogEventDto {
    messageTemplate: string;
    arguments: unknown[];
    level: string;
    enrich: Record<string, string>;
    exception?: { Message: string; Stack: string };
}

interface Log {
    debug(message: string, ...args: unknown[]): void;
    info(message: string, ...args: unknown[]): void;
    warn(message: string, ...args: unknown[]): void;
    error(message: string, ...args: unknown[]): void;
}

window.log = (function (): Log {
    const SUPPRESS_WINDOW_MS = 60_000;
    const recentSends = new Map<string, { lastSent: number; suppressed: number }>();

    function postToServer(
        level: string,
        messageTemplate: string,
        args: unknown[],
        exception?: Error | null,
        extraEnrich?: Record<string, string>
    ): void {
        if (!messageTemplate) return;

        const now = Date.now();
        const dedupeKey = `${level}|${formatMessage(messageTemplate, args || [])}`;
        const prior = recentSends.get(dedupeKey);
        if (prior && now - prior.lastSent < SUPPRESS_WINDOW_MS) {
            prior.suppressed++;
            return;
        }
        const carriedSuppressed = prior ? prior.suppressed : 0;
        recentSends.set(dedupeKey, { lastSent: now, suppressed: 0 });

        if (recentSends.size > 200) {
            recentSends.forEach((entry, key) => {
                if (now - entry.lastSent >= SUPPRESS_WINDOW_MS) recentSends.delete(key);
            });
        }

        // The server binds enrich to a dictionary, and an array of key/value pairs binds empty.
        const enrich: Record<string, string> = { pageUrl: document.location.href };
        if (carriedSuppressed > 0) {
            enrich.suppressedRepeats = String(carriedSuppressed);
        }
        if (extraEnrich) {
            Object.assign(enrich, extraEnrich);
        }

        const logEventDto: LogEventDto = {
            messageTemplate,
            arguments: args || [],
            level,
            enrich
        };

        if (exception) {
            logEventDto.exception = { Message: exception.message, Stack: exception.stack || "" };
        }

        // JSON via fetch, not $.ajax: a jQuery form body binds the argument arrays empty, and would raise the Processing... overlay.
        fetch("/Logging/Ingest", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(logEventDto)
        });
    }

    window.onerror = function (msg: string | Event, url?: string, line?: number, col?: number, error?: Error): void {
        const enrich: Record<string, string> = {};
        if (url) enrich.errorUrl = url;
        if (line !== undefined && line !== null) enrich.errorLine = String(line);
        if (col !== undefined && col !== null) enrich.errorColumn = String(col);
        postToServer("Error", "Unhandled JavaScript error: {Message}", [String(msg)], error, enrich);
    };

    window.addEventListener("unhandledrejection", function (event: PromiseRejectionEvent): void {
        const reason = event.reason;

        if (reason instanceof Error) {
            postToServer("Error", "Unhandled promise rejection: {Reason}", [reason.message || reason], reason);
            return;
        }

        let description: string;
        if (reason === null || reason === undefined) {
            description = String(reason);
        } else if (typeof reason === "string") {
            description = reason;
        } else if (typeof reason === "object") {
            const ctor = (reason.constructor && reason.constructor.name) || "Object";
            let detail: string;
            try {
                detail = JSON.stringify(reason, Object.getOwnPropertyNames(reason));
            } catch {
                detail = String(reason);
            }
            description = `${ctor}: ${detail}`;
        } else {
            description = String(reason);
        }

        postToServer("Error", "Unhandled promise rejection: {Reason}", [description]);
    });

    function formatMessage(template: string, args: unknown[]): string {
        let i = 0;
        return template.replace(/\{[^}]+\}/g, () => i < args.length ? String(args[i++]) : "");
    }

    return {
        debug(message: string, ...args: unknown[]): void {
            console.debug(formatMessage(message, args));
            postToServer("Debug", message, args);
        },
        info(message: string, ...args: unknown[]): void {
            console.info(formatMessage(message, args));
            postToServer("Information", message, args);
        },
        warn(message: string, ...args: unknown[]): void {
            console.warn(formatMessage(message, args));
            postToServer("Warning", message, args);
        },
        error(message: string, ...args: unknown[]): void {
            console.error(formatMessage(message, args));
            postToServer("Error", message, args);
        }
    };
})();

declare var log: Log;
